import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle2, TrendingDown, ChevronDown, ChevronUp, ShoppingCart } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { estimateMonthlySavings, formatINRCompact } from '@/lib/savings';
import { AssumptionsFooter } from '@/components/AssumptionsDrawer';
import { useState, useEffect } from 'react';
import { fetchCatalogProducts } from '@/lib/catalogApi';

export function SolutionPageTemplate({ slug, title, subtitle, description, heroImage, path, benefits, howItWorks, faqs, sectorLabel, useCaseParam, products: productFilter, proofSection }) {
  const [products, setProducts] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const savings = estimateMonthlySavings(500);

  useEffect(() => {
    fetchCatalogProducts().then(({ products: data }) => {
      const normalized = data.map(p => ({ ...p, sku: p.slug || p.sku }));
      const filtered = productFilter ? normalized.filter(productFilter) : normalized.slice(0, 3);
      setProducts(filtered);
    });
  }, []);

  const quoteLink = `/quote?sector=${sectorLabel || slug}&use_case=${useCaseParam || 'dg_replacement'}`;

  return (
    <div data-testid={`solution-page-${slug}`}>
      <SEO title={title} path={path} description={description} />

      {/* Savings Hero */}
      <section className="py-14 lg:py-20">
        <div className="voltide-container">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="animate-fade-up">
              <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-blue-50 text-[#0061DD] border-blue-100 mb-5">
                {subtitle}
              </Badge>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-5">
                {title}
              </h1>
              <p className="text-base text-[#57534E] leading-relaxed mb-5 max-w-xl">{description}</p>

              <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50/60 border border-emerald-100 mb-6" data-testid="hero-savings-band">
                <TrendingDown className="w-6 h-6 text-emerald-600 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-emerald-800">
                    Save {formatINRCompact(savings.low)} – {formatINRCompact(savings.high)}/mo
                  </p>
                  <p className="text-xs text-emerald-600">vs diesel generator · based on 500 kWh BESS</p>
                </div>
              </div>
              <AssumptionsFooter />

              <div className="flex flex-wrap gap-3 mt-6">
                <Link to={quoteLink}>
                  <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-7 py-5 text-base font-semibold gap-2" data-testid="hero-quote-btn">
                    Get a Quote <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="rounded-full border-stone-200 text-[#57534E] hover:text-[#0061DD] hover:border-[#0061DD] px-7 py-5 text-base font-medium gap-2">
                    <ShoppingCart className="w-4 h-4" /> Browse Products
                  </Button>
                </Link>
              </div>
            </div>
            <div className="animate-fade-up stagger-2">
              <img src={heroImage} alt={title} className="w-full rounded-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      {benefits && (
        <section className="py-14 lg:py-18 bg-white">
          <div className="voltide-container">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-8 text-center">Key Benefits</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {benefits.map((b, i) => (
                <div key={i} className="bento-card p-5">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mb-3">
                    <b.icon className="w-5 h-5 text-[#0061DD]" />
                  </div>
                  <h3 className="text-base font-bold text-[#1A1A1A] mb-1">{b.title}</h3>
                  <p className="text-sm text-[#57534E] leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How It Works */}
      {howItWorks && (
        <section className="py-14 lg:py-18">
          <div className="voltide-container">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-8 text-center">How It Works</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {howItWorks.map((step, i) => (
                <div key={i} className="bento-card p-6 text-center">
                  <span className="mono text-3xl font-bold text-[#0061DD]/20">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="text-lg font-bold text-[#1A1A1A] mt-2 mb-2">{step.title}</h3>
                  <p className="text-sm text-[#57534E] leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Typical Configurations */}
      {products.length > 0 && (
        <section className="py-14 lg:py-18 bg-white" data-testid="typical-configs">
          <div className="voltide-container">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-2 text-center">Typical Configurations</h2>
            <p className="text-sm text-[#57534E] text-center mb-8">Popular BESS systems for this use case</p>
            <div className="grid md:grid-cols-3 gap-5">
              {products.slice(0, 3).map(p => {
                const s = estimateMonthlySavings(p.battery_kwh);
                const buyPrice = p.buy_from_price_ex_gst;
                const rentPerKwh = p.rent_from_inr_per_kwh_month;
                return (
                  <Link to={`/products/${p.sku}`} key={p.sku} className="bento-card overflow-hidden group" data-testid={`config-card-${p.sku}`}>
                    <div className="aspect-[4/3] bg-[#F9F9F7] flex items-center justify-center p-6">
                      <img src={p.image} alt={p.product_name} className="max-h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4 space-y-2">
                      <h3 className="text-sm font-bold text-[#1A1A1A] group-hover:text-[#0061DD] transition-colors">{p.product_name}</h3>
                      <div className="flex gap-2">
                        <Badge className="rounded-full bg-blue-50 text-[#0061DD] text-xs">{p.pcs_kw} kW</Badge>
                        <Badge className="rounded-full bg-stone-100 text-[#57534E] text-xs">{p.battery_kwh} kWh</Badge>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-emerald-700">
                        <TrendingDown className="w-3 h-3" /> Save {formatINRCompact(s.low)}–{formatINRCompact(s.high)}/mo
                      </div>
                      <p className="text-xs text-[#A8A29E] mono">
                        {buyPrice ? `Buy from ${formatINRCompact(buyPrice)}` : 'Buy: Request quote'}
                        {' · '}
                        {rentPerKwh ? `Rent from ₹${rentPerKwh}/kWh/mo` : 'Rent: Request quote'}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Proof / Case Study */}
      {proofSection || (
        <section className="py-14 lg:py-18">
          <div className="voltide-container">
            <div className="bento-card overflow-hidden">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="aspect-video md:aspect-auto overflow-hidden">
                  <img src="/images/uru-hero.jpg" alt="Uru Brewpark Case Study" className="w-full h-full object-cover" />
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <Badge className="rounded-full bg-emerald-50 text-emerald-700 border-emerald-100 text-xs w-fit mb-3">Case Study</Badge>
                  <h3 className="text-xl font-bold text-[#1A1A1A] mb-2">Uru Brewpark, Bangalore</h3>
                  <p className="text-sm text-[#57534E] leading-relaxed mb-4">India's first diesel-free large-format restaurant, powered entirely by Voltide BESS.</p>
                  <Link to="/case-studies/uru">
                    <Button variant="outline" className="rounded-full border-[#0061DD] text-[#0061DD] gap-2 text-sm w-fit">
                      Read Case Study <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqs && faqs.length > 0 && (
        <section className="py-14 lg:py-18 bg-white" data-testid="faqs">
          <div className="voltide-container max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bento-card overflow-hidden" data-testid={`faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left p-5 flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#1A1A1A] pr-4">{faq.q}</span>
                    {openFaq === i ? <ChevronUp className="w-4 h-4 text-[#A8A29E] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#A8A29E] shrink-0" />}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 -mt-1">
                      <p className="text-sm text-[#57534E] leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sticky CTA */}
      <section className="py-14 lg:py-18 bg-[#0061DD]">
        <div className="voltide-container text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{title.includes('?') ? title : `Ready for ${title.toLowerCase()}?`}</h2>
          <p className="text-base text-blue-100 mb-6 max-w-lg mx-auto">Get a customized BESS quote for your facility. Our team responds within 48 hours.</p>
          <Link to={quoteLink}>
            <Button className="rounded-full bg-white text-[#0061DD] hover:bg-blue-50 px-8 py-6 text-lg font-semibold gap-2" data-testid="bottom-quote-btn">
              Get a Quote <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
